import React from "react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

/**
 * Reusable horizontal tab bar that respects the global toolbarStyle setting.
 * Props:
 *   tabs: [{ id, label, icon: LucideIcon }]
 *   activeTab: string
 *   onTabChange: (tabId) => void
 */
export const TabBar = ({ tabs, activeTab, onTabChange }) => {
  const { palette, toolbarStyle } = useApp();

  return (
    <div
      style={{
        display: "flex",
        gap: toolbarStyle === "icon-dot" ? 4 : 6,
        padding: "8px 12px",
        background: palette.toolbarBg,
        borderBottom: `1px solid ${palette.cardBorder}`,
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        flexShrink: 0,
        justifyContent: "center",
      }}
    >
      {tabs.map((tab) => {
        const TabIcon = tab.icon;
        const isActive = activeTab === tab.id;
        const tabColor = palette.accent1;

        if (toolbarStyle === "solid") {
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "7px 14px",
                borderRadius: 8,
                border: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                background: isActive ? tabColor : `${tabColor}18`,
                color: isActive ? "#fff" : palette.text2,
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: isActive ? `0 2px 6px ${tabColor}30` : "none",
              }}
            >
              <TabIcon size={ICON_SIZES.sm} />
              {tab.label}
            </button>
          );
        }

        if (toolbarStyle === "pill") {
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "7px 16px",
                borderRadius: 20,
                whiteSpace: "nowrap",
                flexShrink: 0,
                border: `2px solid ${isActive ? tabColor : `${tabColor}40`}`,
                background: isActive ? tabColor : `${tabColor}10`,
                color: isActive ? "#fff" : tabColor,
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <TabIcon size={ICON_SIZES.sm} />
              {tab.label}
            </button>
          );
        }

        // Default: icon-dot
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              padding: "6px 12px",
              borderRadius: 10,
              border: "none",
              flexShrink: 0,
              background: isActive ? `${tabColor}15` : "transparent",
              cursor: "pointer",
              transition: "all 0.2s",
              position: "relative",
            }}
          >
            <div style={{ position: "relative" }}>
              <TabIcon
                size={ICON_SIZES.md}
                style={{ color: isActive ? tabColor : palette.text1 }}
              />
              <div
                style={{
                  position: "absolute",
                  top: -2,
                  right: -4,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: tabColor,
                  border: "2px solid white",
                  boxShadow: `0 0 0 1px ${tabColor}40`,
                }}
              />
            </div>
            <span
              style={{
                fontSize: "0.6rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isActive ? tabColor : palette.text2,
                fontFamily: FONT_FAMILY,
                letterSpacing: "0.3px",
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
